/**
 * Date Formatter Helper with Multi-language Support
 * Centralizes all date formatting logic for reports with locale support
 * Prepared for future internationalization
 */

import { format, fromUnixTime } from 'date-fns';
// Import locales with explicit index.js for compatibility
import * as ptBRModule from 'date-fns/locale/pt-BR/index.js';
import * as enUSModule from 'date-fns/locale/en-US/index.js';
import * as esModule from 'date-fns/locale/es/index.js';

// Handle both default export and named exports for cross-environment compatibility
const ptBR = ptBRModule.default || ptBRModule;
const enUS = enUSModule.default || enUSModule;
const es = esModule.default || esModule;

// Locale mapping - easily extendable for new languages
const LOCALE_MAP = {
  'pt-BR': ptBR,
  'pt': ptBR,
  'en': enUS,
  'en-US': enUS,
  'es': es,
  'es-ES': es,
};

// Date format patterns per locale - easily configurable
const DATE_FORMATS = {
  'pt-BR': {
    dayMonth: 'dd-MMM',           // 15-nov
    monthYear: 'MMM-yyyy',        // nov-2024
    fullDate: "d 'de' MMM 'de' yyyy", // 15 de nov de 2024
    shortDate: 'dd/MM/yyyy',      // 15/11/2024
    fileDate: 'dd-MM-yyyy',       // 15-11-2024
    weekRange: 'dd MMM',          // 15 nov
    year: 'yyyy',                 // 2024
  },
  'en-US': {
    dayMonth: 'dd-MMM',
    monthYear: 'MMM-yyyy',
    fullDate: 'MMM d, yyyy',
    shortDate: 'MM/dd/yyyy',
    fileDate: 'MM-dd-yyyy',
    weekRange: 'MMM dd',
    year: 'yyyy',
  },
  'es-ES': {
    dayMonth: 'dd-MMM',
    monthYear: 'MMM-yyyy',
    fullDate: "d 'de' MMM 'de' yyyy",
    shortDate: 'dd/MM/yyyy',
    fileDate: 'dd-MM-yyyy',
    weekRange: 'dd MMM',
    year: 'yyyy',
  }
};

/**
 * Get current locale from application or default to pt-BR
 * @returns {string} Current locale string
 */
const getCurrentLocale = () => {
  if (typeof window !== 'undefined') {
    try {
      // Method 1: Try to get from Chatwoot Vue app store
      const vueApp = window.chatwoot?.Vue?.app || window.App || document.querySelector('#app')?.__vue_app__;
      if (vueApp) {
        const store = vueApp.config?.globalProperties?.$store || vueApp.$store;
        if (store?.state) {
          // Get account ID from route
          const accountId = store.state.route?.params?.accountId;
          if (accountId && store.getters?.['accounts/getAccount']) {
            const account = store.getters['accounts/getAccount'](accountId);
            if (account?.locale && LOCALE_MAP[account.locale]) {
              return account.locale;
            }
          }

          // Fallback to current user locale
          const currentUser = store.state.auth?.currentUser || store.state.currentUser;
          if (currentUser?.locale && LOCALE_MAP[currentUser.locale]) {
            return currentUser.locale;
          }
        }
      }
    } catch (e) {
      console.debug('[DateFormatter] Could not access Vue store for locale');
    }

    // Method 2: Check localStorage for saved account preference
    const savedLocale = localStorage.getItem('account_locale') || localStorage.getItem('userLocale');
    if (savedLocale && LOCALE_MAP[savedLocale]) {
      return savedLocale;
    }
  }

  // Default to pt-BR (as configured for this installation)
  return 'pt-BR';
};

/**
 * Get locale object for date-fns
 * @param {string} [localeCode] - Optional locale code
 * @returns {object} Locale object for date-fns
 */
const getLocale = (localeCode) => {
  const code = localeCode || getCurrentLocale();
  return LOCALE_MAP[code] || LOCALE_MAP['pt-BR'];
};

/**
 * Get date format pattern for current locale
 * @param {string} formatType - Type of format needed
 * @param {string} [localeCode] - Optional locale code
 * @returns {string} Format pattern string
 */
const getDateFormat = (formatType, localeCode) => {
  const code = localeCode || getCurrentLocale();
  const formats = DATE_FORMATS[code] || DATE_FORMATS['pt-BR'];
  return formats[formatType] || formats.fullDate;
};

/**
 * Format date for charts based on grouping period
 * @param {number|Date} timestamp - Unix timestamp or Date object
 * @param {string} period - Grouping period (day, week, month, year)
 * @param {string} [localeCode] - Optional locale code
 * @returns {string} Formatted date string
 */
export const formatDateChart = (timestamp, period = 'day', localeCode) => {
  const date = typeof timestamp === 'number' ? fromUnixTime(timestamp) : timestamp;
  const locale = getLocale(localeCode);

  let formatPattern;
  switch (period) {
    case 'week':
      formatPattern = getDateFormat('weekRange', localeCode);
      break;
    case 'month':
      formatPattern = getDateFormat('monthYear', localeCode);
      break;
    case 'year':
      formatPattern = getDateFormat('year', localeCode);
      break;
    default: // day
      formatPattern = getDateFormat('dayMonth', localeCode);
  }

  return format(date, formatPattern, { locale });
};

/**
 * Format date range for display
 * @param {number|Date} startTimestamp - Start date
 * @param {number|Date} endTimestamp - End date
 * @param {string} [localeCode] - Optional locale code
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startTimestamp, endTimestamp, localeCode) => {
  const startDate = typeof startTimestamp === 'number' ? fromUnixTime(startTimestamp) : startTimestamp;
  const endDate = typeof endTimestamp === 'number' ? fromUnixTime(endTimestamp) : endTimestamp;
  const locale = getLocale(localeCode);
  const formatPattern = getDateFormat('weekRange', localeCode);

  const start = format(startDate, formatPattern, { locale });
  const end = format(endDate, formatPattern, { locale });

  return `${start} - ${end}`;
};

/**
 * Format date for tooltips and detailed displays
 * @param {number|Date} timestamp - Unix timestamp or Date object
 * @param {string} [localeCode] - Optional locale code
 * @returns {string} Formatted date string
 */
export const formatDateTooltip = (timestamp, localeCode) => {
  const date = typeof timestamp === 'number' ? fromUnixTime(timestamp) : timestamp;
  const locale = getLocale(localeCode);
  const formatPattern = getDateFormat('fullDate', localeCode);

  return format(date, formatPattern, { locale });
};

/**
 * Format date for file names
 * @param {number|Date} timestamp - Unix timestamp or Date object
 * @param {string} [localeCode] - Optional locale code
 * @returns {string} Formatted date string for file names
 */
export const formatDateFileName = (timestamp, localeCode) => {
  const date = typeof timestamp === 'number' ? fromUnixTime(timestamp) : timestamp;
  const locale = getLocale(localeCode);
  const formatPattern = getDateFormat('fileDate', localeCode);

  return format(date, formatPattern, { locale });
};

/**
 * Format week range from a date
 * @param {number|Date} timestamp - Unix timestamp or Date object
 * @param {string} [localeCode] - Optional locale code
 * @returns {object} Object with formatted start and end dates
 */
export const formatWeekRange = (timestamp, localeCode) => {
  const date = typeof timestamp === 'number' ? fromUnixTime(timestamp) : new Date(timestamp);
  const firstDay = date.getDate() - date.getDay();
  const lastDay = firstDay + 6;

  const weekFirstDate = new Date(date.setDate(firstDay));
  const weekLastDate = new Date(date.setDate(lastDay));

  return formatDateRange(weekFirstDate, weekLastDate, localeCode);
};

/**
 * Get localized month names
 * @param {string} [localeCode] - Optional locale code
 * @returns {array} Array of month names
 */
export const getMonthNames = (localeCode) => {
  const locale = getLocale(localeCode);
  const months = [];

  for (let i = 0; i < 12; i++) {
    const date = new Date(2024, i, 1);
    months.push(format(date, 'MMMM', { locale }));
  }

  return months;
};

/**
 * Get localized day names
 * @param {string} [localeCode] - Optional locale code
 * @param {boolean} [short] - Return short names (Mon) instead of full (Monday)
 * @returns {array} Array of day names
 */
export const getDayNames = (localeCode, short = false) => {
  const locale = getLocale(localeCode);
  const days = [];
  const formatPattern = short ? 'EEE' : 'EEEE';

  // Start from Sunday (0) to Saturday (6)
  for (let i = 0; i < 7; i++) {
    // Use a fixed week to get day names
    const date = new Date(2024, 0, i + 7); // January 7-13, 2024 (Sunday to Saturday)
    days.push(format(date, formatPattern, { locale }));
  }

  return days;
};

/**
 * Set application locale (saves to localStorage)
 * @param {string} localeCode - Locale code to set
 */
export const setLocale = (localeCode) => {
  if (typeof window !== 'undefined' && LOCALE_MAP[localeCode]) {
    localStorage.setItem('userLocale', localeCode);
  }
};

// Export utility function for direct format with locale
export const formatWithLocale = (date, formatPattern, localeCode) => {
  const locale = getLocale(localeCode);
  const dateObj = typeof date === 'number' ? fromUnixTime(date) : date;
  return format(dateObj, formatPattern, { locale });
};

// Export current locale getter
export { getCurrentLocale, getLocale };

// Default export with all main functions
export default {
  formatDateChart,
  formatDateRange,
  formatDateTooltip,
  formatDateFileName,
  formatWeekRange,
  formatWithLocale,
  getMonthNames,
  getDayNames,
  getCurrentLocale,
  setLocale,
};